import { useState, useEffect } from "react";
import { Table, TableAvailability, TableWeekdayAvailability, TableBookablePeriod } from "@/types/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Plus, CalendarOff } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getDayName, getDayShortName } from "@/lib/tableAvailabilityUtils";
import { checkWeekdayAvailabilityImpact, checkSpecificDateImpact, ImpactedReservation } from "@/lib/availabilityImpactUtils";
import { AvailabilityImpactDialog } from "./AvailabilityImpactDialog";
import { useToast } from "@/hooks/use-toast";
import { DateRange } from "react-day-picker";

interface TableAvailabilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: Table | null;
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  bookablePeriods?: TableBookablePeriod[];
  onAddBlocked: (tableId: string, date: Date, reason?: string) => void;
  onRemoveBlocked: (id: string) => void;
  onSetWeekdayAvailability: (tableId: string, availableDays: number[], reason?: string) => void;
  onAddBookablePeriod?: (tableId: string, startDate: Date, endDate: Date, reason?: string) => Promise<boolean>;
  onRemoveBookablePeriod?: (periodId: string) => Promise<boolean>;
  onEditReservation: (reservationId: string) => void;
  establishmentId?: string;
}

export function TableAvailabilityDialog({
  open,
  onOpenChange,
  table,
  availabilities,
  weekdayAvailabilities,
  bookablePeriods = [],
  onAddBlocked,
  onRemoveBlocked,
  onSetWeekdayAvailability,
  onAddBookablePeriod,
  onRemoveBookablePeriod,
  onEditReservation,
  establishmentId,
}: TableAvailabilityDialogProps) {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [reason, setReason] = useState("");
  const [availableDays, setAvailableDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [weekdayReason, setWeekdayReason] = useState("");
  
  // Bookable periods state
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [periodReason, setPeriodReason] = useState("");
  const [isAddingPeriod, setIsAddingPeriod] = useState(false);
  
  // Impact dialog state
  const [showImpactDialog, setShowImpactDialog] = useState(false);
  const [impactedReservations, setImpactedReservations] = useState<ImpactedReservation[]>([]);
  const [pendingAction, setPendingAction] = useState<{
    type: 'weekday' | 'specific_date';
    data: any;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedDate(undefined);
      setReason("");
      setDateRange(undefined);
      setPeriodReason("");
    }
  }, [open]);

  useEffect(() => {
    if (table && open) {
      const existing = weekdayAvailabilities.find(w => w.tableId === table.id);
      if (existing) {
        setAvailableDays(existing.availableDays);
        setWeekdayReason(existing.reason || "");
      } else {
        setAvailableDays([0, 1, 2, 3, 4, 5, 6]);
        setWeekdayReason("");
      }
    }
  }, [table, open, weekdayAvailabilities]);

  const tableAvailabilities = table
    ? availabilities.filter((a) => a.tableId === table.id)
    : [];
    
  const tableBookablePeriods = table
    ? bookablePeriods.filter((p) => p.tableId === table.id)
    : [];

  const handleAddBlocked = () => {
    if (table && selectedDate) {
      onAddBlocked(table.id, selectedDate, reason.trim() || undefined);
      setSelectedDate(undefined);
      setReason("");
    }
  };

  const handleDayToggle = (day: number, checked: boolean | "indeterminate") => {
    if (checked === true) {
      setAvailableDays([...availableDays, day]);
    } else {
      setAvailableDays(availableDays.filter(d => d !== day));
    }
  };

  const handleCheckWeekdayImpact = async () => {
    if (!table) return;
    
    const impact = await checkWeekdayAvailabilityImpact(table.id, availableDays, establishmentId);
    
    if (impact.hasImpactedReservations) {
      setImpactedReservations(impact.impactedReservations);
      setPendingAction({
        type: 'weekday',
        data: { tableId: table.id, availableDays, reason: weekdayReason }
      });
      setShowImpactDialog(true);
    } else {
      // Pas d'impact, sauvegarder directement
      onSetWeekdayAvailability(table.id, availableDays, weekdayReason.trim() || undefined);
      toast({
        title: "✅ Disponibilité mise à jour",
        description: "Aucune réservation n'est affectée par ce changement.",
      });
    }
  };

  const handleCheckSpecificDateImpact = async () => {
    if (!table || !selectedDate) return;
    
    const impact = await checkSpecificDateImpact(table.id, selectedDate, establishmentId);
    
    if (impact.hasImpactedReservations) {
      setImpactedReservations(impact.impactedReservations);
      setPendingAction({
        type: 'specific_date',
        data: { tableId: table.id, date: selectedDate, reason }
      });
      setShowImpactDialog(true);
    } else {
      // Pas d'impact, bloquer directement
      onAddBlocked(table.id, selectedDate, reason.trim() || undefined);
      setSelectedDate(undefined);
      setReason("");
      toast({
        title: "✅ Disponibilité mise à jour",
        description: "Aucune réservation n'est affectée par ce changement.",
      });
    }
  };

  const handleConfirmAvailabilityChange = () => {
    if (!pendingAction) return;
    
    if (pendingAction.type === 'weekday') {
      const { tableId, availableDays, reason } = pendingAction.data;
      onSetWeekdayAvailability(tableId, availableDays, reason.trim() || undefined);
    } else {
      const { tableId, date, reason } = pendingAction.data;
      onAddBlocked(tableId, date, reason.trim() || undefined);
      setSelectedDate(undefined);
      setReason("");
    }
    
    toast({
      title: "✅ Disponibilité mise à jour",
      description: "Les réservations existantes n'ont pas été modifiées.",
    });
    
    setShowImpactDialog(false);
    setPendingAction(null);
    setImpactedReservations([]);
  };

  const handleEditReservationFromImpact = (reservationId: string) => {
    // Fermer tous les dialogues (option A)
    setShowImpactDialog(false);
    onOpenChange(false);
    onEditReservation(reservationId);
  };

  const handleAddBookablePeriod = async () => {
    if (!table || !dateRange?.from || !dateRange?.to || !onAddBookablePeriod) return;
    
    setIsAddingPeriod(true);
    const success = await onAddBookablePeriod(
      table.id,
      dateRange.from,
      dateRange.to,
      periodReason.trim() || undefined
    );
    
    if (success) {
      setDateRange(undefined);
      setPeriodReason("");
    }
    setIsAddingPeriod(false);
  };

  const handleRemoveBookablePeriod = async (periodId: string) => {
    if (!onRemoveBookablePeriod) return;
    await onRemoveBookablePeriod(periodId);
  };

  const disabledDates = tableAvailabilities.map((a) => a.date);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Gérer la disponibilité - {table?.name}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="weekdays" className="w-full mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="weekdays">Jours semaine</TabsTrigger>
            <TabsTrigger value="specific">Dates bloquées</TabsTrigger>
            <TabsTrigger value="bookable">
              <CalendarOff className="w-4 h-4 mr-1" />
              Hors résa
            </TabsTrigger>
          </TabsList>
          
          {/* Onglet 1 : Jours de la semaine */}
          <TabsContent value="weekdays" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Jours d'ouverture de cette table</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Sélectionnez les jours où cette table est disponible
                </p>
              </div>

              {/* Préréglages rapides */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setAvailableDays([1, 2, 3, 4, 5])}
                >
                  Semaine (Lun-Ven)
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setAvailableDays([6, 0])}
                >
                  Week-end
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setAvailableDays([0, 1, 2, 3, 4, 5, 6])}
                >
                  Tous les jours
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setAvailableDays([])}
                >
                  Aucun jour
                </Button>
              </div>

              {/* Sélection des jours */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { day: 1, label: "Lundi" },
                  { day: 2, label: "Mardi" },
                  { day: 3, label: "Mercredi" },
                  { day: 4, label: "Jeudi" },
                  { day: 5, label: "Vendredi" },
                  { day: 6, label: "Samedi" },
                  { day: 0, label: "Dimanche" },
                ].map(({ day, label }) => (
                  <div key={day} className="flex items-center space-x-2">
                    <Checkbox
                      id={`day-${day}`}
                      checked={availableDays.includes(day)}
                      onCheckedChange={(checked) => handleDayToggle(day, checked)}
                    />
                    <Label htmlFor={`day-${day}`} className="cursor-pointer font-normal">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
              
              {/* Badge visuel des jours sélectionnés */}
              {availableDays.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-md">
                  <span className="text-sm font-medium">Sélection :</span>
                  {[...availableDays].sort((a, b) => a - b).map(day => (
                    <Badge key={day} variant="secondary">
                      {getDayShortName(day)}
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Raison optionnelle */}
              <div className="space-y-2">
                <Label htmlFor="weekday-reason">Raison (optionnel)</Label>
                <Input
                  id="weekday-reason"
                  value={weekdayReason}
                  onChange={(e) => setWeekdayReason(e.target.value)}
                  placeholder="Ex: Terrasse ouverte uniquement le week-end"
                />
              </div>
              
              <Button onClick={handleCheckWeekdayImpact} className="w-full">
                Enregistrer la disponibilité hebdomadaire
              </Button>
            </div>
          </TabsContent>
          
          {/* Onglet 2 : Dates spécifiques */}
          <TabsContent value="specific" className="space-y-4 mt-4">
            {/* Liste des dates bloquées */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Dates bloquées</h3>
              {tableAvailabilities.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune date bloquée pour cette table
                </p>
              ) : (
                <div className="space-y-2">
                  {tableAvailabilities.map((availability) => (
                    <div
                      key={availability.id}
                      className="flex items-center justify-between p-3 border rounded-md"
                    >
                      <div>
                        <p className="font-medium">
                          {format(availability.date, "EEEE d MMMM yyyy", { locale: fr })}
                        </p>
                        {availability.reason && (
                          <p className="text-sm text-muted-foreground">
                            {availability.reason}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onRemoveBlocked(availability.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Formulaire d'ajout */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-semibold">Bloquer une nouvelle date</h3>
              
              <div className="space-y-2">
                <Label>Sélectionner une date</Label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) =>
                    date < new Date() ||
                    disabledDates.some(
                      (d) => format(d, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
                    )
                  }
                  className="rounded-md border pointer-events-auto"
                  locale={fr}
                />
                {selectedDate && (
                  <Badge variant="outline">
                    {format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}
                  </Badge>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Raison (optionnel)</Label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: Terrasse fermée - pluie, En maintenance..."
                />
              </div>

              <Button
                onClick={handleCheckSpecificDateImpact}
                disabled={!selectedDate}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Bloquer cette date
              </Button>
            </div>
          </TabsContent>
          
          {/* Onglet 3 : Périodes hors réservation automatique */}
          <TabsContent value="bookable" className="space-y-4 mt-4">
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold">Périodes hors réservation automatique</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Pendant ces périodes, la table ne sera pas proposée automatiquement aux clients
                  mais restera assignable manuellement par le staff.
                </p>
              </div>
              
              {/* Liste des périodes existantes */}
              {tableBookablePeriods.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">
                  Aucune période définie. La table est réservable automatiquement.
                </p>
              ) : (
                <div className="space-y-2">
                  {tableBookablePeriods.map((period) => (
                    <div
                      key={period.id}
                      className="flex items-center justify-between p-3 border rounded-md bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800"
                    >
                      <div>
                        <p className="font-medium text-orange-800 dark:text-orange-200">
                          {format(period.startDate, "d MMM yyyy", { locale: fr })} → {format(period.endDate, "d MMM yyyy", { locale: fr })}
                        </p>
                        {period.reason && (
                          <p className="text-sm text-orange-600 dark:text-orange-400">
                            {period.reason}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveBookablePeriod(period.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Formulaire d'ajout de période */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-semibold">Ajouter une période</h3>
              
              <div className="space-y-2">
                <Label>Sélectionner une plage de dates</Label>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  disabled={(date) => date < new Date()}
                  className="rounded-md border pointer-events-auto"
                  locale={fr}
                  numberOfMonths={1}
                />
                {dateRange?.from && dateRange?.to && (
                  <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                    {format(dateRange.from, "d MMM", { locale: fr })} → {format(dateRange.to, "d MMM yyyy", { locale: fr })}
                  </Badge>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="period-reason">Raison (optionnel)</Label>
                <Input
                  id="period-reason"
                  value={periodReason}
                  onChange={(e) => setPeriodReason(e.target.value)}
                  placeholder="Ex: Événement privé, Rénovation..."
                />
              </div>

              <Button
                onClick={handleAddBookablePeriod}
                disabled={!dateRange?.from || !dateRange?.to || isAddingPeriod || !onAddBookablePeriod}
                className="w-full"
              >
                <CalendarOff className="w-4 h-4 mr-2" />
                {isAddingPeriod ? "Ajout..." : "Ajouter cette période"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <AvailabilityImpactDialog
          open={showImpactDialog}
          onOpenChange={(open) => {
            setShowImpactDialog(open);
            if (!open) {
              setPendingAction(null);
              setImpactedReservations([]);
            }
          }}
          tableName={table?.name || ''}
          impactedReservations={impactedReservations}
          onConfirm={handleConfirmAvailabilityChange}
          onEditReservation={handleEditReservationFromImpact}
        />
      </DialogContent>
    </Dialog>
  );
}
